import {
  Object3D,
  SphereGeometry,
  MeshBasicMaterial,
  Mesh,
  Vector3,
  Quaternion,
  Material,
} from "three";

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { TrackingBatPosition } from "mannequin/src/types/tracking/mannequin";

import { Context } from "../context";
import { Frames } from "../util/frames";
import { calculateAlpha } from "../util/clock";
import { makeGradientMap } from "../../lib/misc/util";

const TRANSITION_CEILING = 0.5;
const MAX_NEIGHBOR_TIME = 1;
const MAX_BAT_DIST = 8;
const BAT_SCALE = 0.037;

const batQuat = new Quaternion();
const unitForward = new Vector3(0, -1.0, 0);

export class Bat extends Object3D {
  contextRef: Context;
  handle: Object3D;
  head: Object3D;
  topHand: Object3D;
  bottomHand: Object3D;
  bat: Object3D | undefined;
  batMaterial: Material | undefined;

  firstFrameTime?: number = undefined;
  lastFrameTime?: number = undefined;

  topHandPercent = 0.15;
  bottomHandPercent = 0.05;

  constructor(context: Context) {
    super();
    this.contextRef = context;

    const handleGeometry = new SphereGeometry(0.1, 32, 16);
    const handleMaterial = new MeshBasicMaterial({ color: 0xffff00 });
    this.handle = new Mesh(handleGeometry, handleMaterial);

    const headGeometry = new SphereGeometry(0.2, 32, 16);
    const headMaterial = new MeshBasicMaterial({ color: 0xffff00 });
    this.head = new Mesh(headGeometry, headMaterial);

    const handMaterial = new MeshBasicMaterial({ color: 0xff10f0 });
    this.topHand = new Mesh(handleGeometry, handMaterial);
    this.bottomHand = new Mesh(handleGeometry, handMaterial);

    this.handle.visible = false;
    this.head.visible = false;
    this.topHand.visible = false;
    this.bottomHand.visible = false;

    this.add(this.head);
    this.add(this.handle);
    this.add(this.topHand);
    this.add(this.bottomHand);

    const toonGradientMap = makeGradientMap(5, context.renderer, 100);
    const matCtor = context.userOptions.getMaterialCtor().player;

    const loader = new GLTFLoader().setPath(`${context.options.assetBase}`);
    loader.load("/models/bat.glb", (model) => {
      this.bat = model.scene;
      this.bat.traverse((obj) => {
        if (obj instanceof Mesh) {
          const old = obj.material;
          this.batMaterial = new matCtor();
          this.batMaterial.copy(obj.material);
          obj.material = this.batMaterial;
          old.dispose();

          this.batMaterial!.alphaTest = 0.01;
          this.batMaterial!.transparent = true;
          this.batMaterial!.opacity = 0.0;
        }
      });
      this.bat.position.set(-10, -10, -10);
      this.bat.receiveShadow = context.userOptions.hasShadows();
      this.bat?.scale.setScalar(BAT_SCALE);
      this.add(this.bat);
      this.visible = false;
    });
  }

  update(frames: Frames, time: number) {
    if (
      frames.current &&
      frames.current.inferredBat?.headPosition &&
      frames.current.inferredBat?.handlePosition &&
      !this.batTooFar(frames.current.inferredBat)
    ) {
      this.handleFade(time, true);
      this.visible = this.contextRef.state.batEnabled;

      const neighbors = frames.getNeighbours<TrackingBatPosition>(
        (frame) => frame.inferredBat
      );

      if (
        neighbors &&
        neighbors.prev?.time != undefined &&
        neighbors.next?.time != undefined &&
        neighbors.next.time - neighbors.prev.time < MAX_NEIGHBOR_TIME
      ) {
        const alpha = calculateAlpha(
          [neighbors.prev.time, neighbors.next.time],
          time
        );
        if (
          alpha != undefined &&
          neighbors.next.neighbor.headPosition &&
          neighbors.next.neighbor.handlePosition
        ) {
          let interpBat = interpolateBat(
            neighbors.prev.neighbor,
            neighbors.next.neighbor,
            alpha
          );
          if (interpBat) {
            this.head.position.fromArray(interpBat.headPosition);
            this.handle.position.fromArray(interpBat.handlePosition);

            this.topHand.position.lerpVectors(
              this.handle.position,
              this.head.position,
              this.topHandPercent
            );
            this.bottomHand.position.lerpVectors(
              this.handle.position,
              this.head.position,
              this.bottomHandPercent
            );
          }
        }
      }

      batQuat.setFromUnitVectors(
        unitForward,
        new Vector3()
          .subVectors(this.handle.position, this.head.position)
          .normalize()
      );

      this.bat?.setRotationFromQuaternion(batQuat);

      this.bat?.position.lerpVectors(
        this.head.position,
        this.handle.position,
        0.5
      );
    } else {
      this.handleFade(time, false);
    }

    const batter = this.contextRef.world?.actors.getBatter();
    if (batter && this.bat) {
      const { transY } = batter.transitioner;
      if (transY < 0) {
        this.bat.position.y += transY;
      }
    }
  }

  handleFade(time: number, during: boolean) {
    if (!this.batMaterial) return;

    if (during) {
      if (!this.firstFrameTime) this.firstFrameTime = time;
      const fade = Math.min(time - this.firstFrameTime, TRANSITION_CEILING);
      this.batMaterial.opacity = fade / TRANSITION_CEILING;
      return;
    }

    if (!during && this.firstFrameTime) {
      if (!this.lastFrameTime) this.lastFrameTime = time;
      const fade = Math.min(time - this.lastFrameTime, TRANSITION_CEILING);
      this.batMaterial.opacity = 1.0 - fade / TRANSITION_CEILING;

      if (this.batMaterial.opacity <= 0) {
        this.firstFrameTime = undefined;
        this.lastFrameTime = undefined;
      }
      return;
    }

    this.firstFrameTime = undefined;
    this.lastFrameTime = undefined;
    this.batMaterial.opacity = 0.0;
  }

  batTooFar(bat: TrackingBatPosition): boolean {
    let headDist =
      Math.abs(bat.headPosition[0]) + Math.abs(bat.headPosition[2]);
    let handleDist = Math.abs(
      bat.handlePosition[0] + Math.abs(bat.handlePosition[2])
    );
    return Math.min(headDist, handleDist) > MAX_BAT_DIST;
  }
}

function interpolateBat(
  prev: TrackingBatPosition,
  next: TrackingBatPosition,
  alpha: number
): TrackingBatPosition | undefined {
  if (prev == undefined || !prev.headPosition || !prev.handlePosition)
    return undefined;
  if (next == undefined || !next.headPosition || !next.handlePosition)
    return prev;

  const out: TrackingBatPosition = {
    headPosition: [0, 0, 0],
    handlePosition: [0, 0, 0],
  };

  for (let i = 0; i < 3; i++) {
    out.headPosition[i] =
      prev.headPosition[i] +
      alpha * (next.headPosition[i] - prev.headPosition[i]);
    out.handlePosition[i] =
      prev.handlePosition[i] +
      alpha * (next.handlePosition[i] - prev.handlePosition[i]);
  }

  return out;
}
